import { prependTitleToRawContentState } from '@ms/yammer-content-support';
import type { Entity, EntityMap } from '@ms/yammer-data-support';
import { moveToFulfilled, update } from '@ms/yammer-data-support';
import { limitedReactions } from '@ms/yammer-graphql';
import type { Reaction } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type { CuratedThreadListLoadAction } from '../../domains/curatedThreadList/actions.types';
import type { DeleteFileRejectedAction } from '../../domains/deleteFile/actions.types';
import type { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type {
  AddUpvotePending,
  AddUpvoteRejected,
  AnswerAddUpvoteActions,
  RemoveUpvoteActions,
  RemoveUpvotePending,
  RemoveUpvoteRejected,
} from '../../domains/messageAddRemoveUpvote/actions.types';
import type { PublishModeratedThreadAction } from '../../domains/messageWithModerationState/actions.types';
import type { UpdateModerationStateAction } from '../../domains/messageWithModerationState/updateModerationStateAction.types';
import type {
  MessageLikeAction,
  MessageLikePendingAction,
  MessageLikeRejectedAction,
  MessageRealtimeReaction,
  MessageUnlikeAction,
  MessageUnlikePendingAction,
  ResetFailedFileDeletionAction,
} from '../../domains/messages/actions.types';
import type {
  PublishModeratedEventAgentReplyAction,
  ReconcileModeratedEventAgentReplyAction,
} from '../../domains/moderatedEventAgentReplies/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import type { PollCloseFulfilledAction, PollReopenFulfilledAction } from '../../domains/pollClose/actions.types';
import type {
  PollLoadFulfilledAction,
  PollVotePendingAction,
  PollVoteRejectedAction,
} from '../../domains/polls/actions.types';
import type {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import type {
  ThreadMarkAsQuestionFulfilledAction,
  ThreadUnmarkAsQuestionFulfilledAction,
} from '../../domains/threadMarkAsQuestion/types';
import type { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import type { User } from '../users/types';

import type { Message, Messages, NormalMessageContent } from './types';
import { isPollMessageContent } from './types';
export type MessageAction =
  | ReconcileModeratedEventAgentReplyAction
  | PublishModeratedThreadAction
  | PublishModeratedEventAgentReplyAction
  | UpdateModerationStateAction
  | DeleteFileRejectedAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | MessageLikeAction
  | MessageUnlikeAction
  | MessageRealtimeReaction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | ThreadMarkAsQuestionFulfilledAction
  | ThreadUnmarkAsQuestionFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | PollCloseFulfilledAction
  | PollReopenFulfilledAction
  | PollVotePendingAction
  | PollVoteRejectedAction
  | PollLoadFulfilledAction
  | DeleteMessageFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | AnswerAddUpvoteActions
  | RemoveUpvoteActions
  | ResetFailedFileDeletionAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | CuratedThreadListLoadAction;
const initialState: Messages = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};
const maxFeaturedIds = 8;
const stateWithUpdatedReactions = (
  state: EntityMap<Message>,
  id: Message['id'],
  updatedReactionState: Partial<Message>
) => update(state, id, updatedReactionState);
const addReaction = (message: Message, reaction: Reaction): Message => ({
  ...message,
  reactionsCounts: {
    ...message.reactionsCounts,
    [reaction]: message.reactionsCounts[reaction] + 1,
  },
});
const removeReaction = (message: Message, reaction: Reaction): Message => ({
  ...message,
  reactionsCounts: {
    ...message.reactionsCounts,
    [reaction]: message.reactionsCounts[reaction] - 1,
  },
});
const removeFeaturedReactionUserId = (message: Message, userId: User['id'], reaction: Reaction): Message => ({
  ...message,
  featuredUserIdsByReaction: {
    ...message.featuredUserIdsByReaction,
    [reaction]: message.featuredUserIdsByReaction[reaction].filter((id) => id !== userId),
  },
});
const addUserLikeToMessage = (state: Messages, action: MessageLikePendingAction) => {
  const { id, reaction } = action.payload;
  const message = state.fulfilled.byId[id];
  const { reactionsTotalCount, viewerReaction } = message;
  if (reaction !== viewerReaction) {
    const unlikedMessage = viewerReaction != null ? removeReaction(message, viewerReaction) : message;
    const changedReactionMessage = addReaction(unlikedMessage, reaction);
    const updatedReactionState = {
      ...changedReactionMessage,
      reactionsTotalCount: reactionsTotalCount + (viewerReaction != null ? 0 : 1),
      viewerHasLiked: true,
      viewerReaction: reaction,
    };

    return stateWithUpdatedReactions(state, id, updatedReactionState);
  }

  return stateWithUpdatedReactions(state, id, {});
};
const removeUserLikeFromMessage = (state: Messages, action: MessageLikeRejectedAction | MessageUnlikePendingAction) => {
  const { id } = action.payload;
  const message = state.fulfilled.byId[id];
  const { reactionsTotalCount, viewerReaction } = message;
  if (viewerReaction != null) {
    const unlikedMessage = removeReaction(message, viewerReaction);
    const updatedReactionState = {
      ...unlikedMessage,
      reactionsTotalCount: reactionsTotalCount - 1,
      viewerHasLiked: false,
      viewerReaction: undefined,
    };

    return stateWithUpdatedReactions(state, id, updatedReactionState);
  }

  return stateWithUpdatedReactions(state, id, {});
};
const removeFeaturedReaction = (message: Message, userId: User['id'], reaction: Reaction): Message => {
  const { reactionsTotalCount, featuredReactionUserIds } = message;
  let updatedState = removeFeaturedReactionUserId(message, userId, reaction);
  updatedState = removeReaction(updatedState, reaction);
  const updatedMessage = {
    ...updatedState,
    featuredReactionUserIds: featuredReactionUserIds.filter((id) => id !== userId),
    reactionsTotalCount: reactionsTotalCount - 1,
  };

  return updatedMessage;
};
const removeFeaturedUserIdWithoutReaction = (message: Message, userId: User['id']): Message => {
  const { featuredUserIdsByReaction } = message;
  let updatedMessage = message;
  for (const reaction of limitedReactions) {
    if (featuredUserIdsByReaction[reaction].includes(userId)) {
      updatedMessage = removeFeaturedReaction(message, userId, reaction);
    }
  }

  return updatedMessage;
};
const addFeaturedReaction = (message: Message, reaction: Reaction, userId: User['id']): Message => {
  const { reactionsTotalCount, featuredReactionUserIds, featuredUserIdsByReaction } = message;
  const updatedFeaturedReactionUserIds =
    featuredReactionUserIds.length < maxFeaturedIds ? [...featuredReactionUserIds, userId] : featuredReactionUserIds;
  const updatedFeaturedUserIdsByReaction =
    featuredReactionUserIds.length < maxFeaturedIds
      ? [...featuredUserIdsByReaction[reaction], userId]
      : featuredUserIdsByReaction[reaction];
  const updatedMessage = {
    ...message,
    featuredReactionUserIds: updatedFeaturedReactionUserIds,
    featuredUserIdsByReaction: {
      ...message.featuredUserIdsByReaction,
      [reaction]: updatedFeaturedUserIdsByReaction,
    },
    reactionsTotalCount: reactionsTotalCount + 1,
  };

  return addReaction(updatedMessage, reaction);
};
const addRealtimeReaction = (state: Messages, action: MessageRealtimeReaction) => {
  const { id, reaction, userId } = action.payload;
  const message = state.fulfilled.byId[id];
  if (!message) {
    return state;
  }
  const updatedReactionState = removeFeaturedUserIdWithoutReaction(message, userId);
  const finalReactionState = addFeaturedReaction(updatedReactionState, reaction, userId);

  return stateWithUpdatedReactions(state, id, finalReactionState);
};
const deleteRealtimeReaction = (state: Messages, action: MessageRealtimeReaction) => {
  const { id, reaction, userId } = action.payload;
  const message = state.fulfilled.byId[id];
  if (!message) {
    return state;
  }
  const updatedReactionState = removeFeaturedReaction(message, userId, reaction);

  return stateWithUpdatedReactions(state, id, updatedReactionState);
};
const updatePoll = (state: Messages, action: PollLoadFulfilledAction) => {
  const { payload } = action;
  const message = state.fulfilled.byId[payload.id];
  const messageWithUpdatedPollVotes = {
    ...message,
    content: {
      ...message.content,
      answers: payload.content.answers,
      viewerCanClosePoll: payload.content.viewerCanClosePoll,
      isPollClosed: payload.content.isPollClosed,
    },
  };

  return update(state, payload.id, messageWithUpdatedPollVotes);
};
export interface EntityWithContent extends Entity {
  readonly content: Message['content'];
}
export type OptimisticallyUpdatePoll = <T extends EntityWithContent>(
  state: EntityMap<T>,
  action: PollVotePendingAction
) => EntityMap<T>;
export const optimisticallyUpdatePoll: OptimisticallyUpdatePoll = <T extends EntityWithContent>(
  state: EntityMap<T>,
  { payload }: PollVotePendingAction
) => {
  const entity = state.fulfilled.byId[payload.id];
  if (!entity) {
    return state;
  }
  const content = entity.content;
  if (!isPollMessageContent(content)) {
    return state;
  }
  const entityWithUpdatedContent = {
    ...entity,
    content: {
      ...content,
      answers: content.answers.map((answer) => {
        if (answer.index === payload.index) {
          return {
            ...answer,
            isSelected: true,
            voteCount: answer.isSelected ? answer.voteCount : answer.voteCount + 1,
          };
        }

        return {
          ...answer,
          isSelected: false,
          voteCount: answer.isSelected ? answer.voteCount - 1 : answer.voteCount,
        };
      }),
    },
  };

  return update(state, payload.id, entityWithUpdatedContent);
};
export type RevertOptimisticallyUpdatedPoll = <T extends EntityWithContent>(
  state: EntityMap<T>,
  action: PollVoteRejectedAction
) => EntityMap<T>;
export const revertOptimisticallyUpdatedPoll: RevertOptimisticallyUpdatedPoll = <T extends EntityWithContent>(
  state: EntityMap<T>,
  { payload }: PollVoteRejectedAction
) => {
  const entity = state.fulfilled.byId[payload.id];
  if (!entity) {
    return state;
  }
  const content = entity.content;
  if (!isPollMessageContent(content)) {
    return state;
  }
  const entityWithUpdatedContent = {
    ...entity,
    content: {
      ...content,
      answers: content.answers.map((answer) => {
        if (answer.index === payload.index) {
          return {
            ...answer,
            isSelected: false,
            voteCount: answer.voteCount - 1,
          };
        }

        if (answer.index === payload.initialVoteIndex) {
          return {
            ...answer,
            isSelected: true,
            voteCount: answer.voteCount + 1,
          };
        }

        return {
          ...answer,
          isSelected: false,
        };
      }),
    },
  };

  return update(state, payload.id, entityWithUpdatedContent);
};
const markMessageAsDeleted = (
  state: Messages,
  { messageId, deletionOutcome, parentMessageId }: DeleteMessageFulfilledAction['payload']
) => {
  if (deletionOutcome === 'MESSAGE_PLACEHOLDER_RETAINED') {
    return update(state, messageId, { isDeleted: true });
  }

  if (deletionOutcome === 'MESSAGE_REMOVED') {
    return update(state, messageId, { isDeleted: true });
  }

  if (deletionOutcome === 'THREAD_REMOVED') {
    return update(state, messageId, { isDeleted: true });
  }

  if (deletionOutcome === 'MESSAGE_AND_PARENT_REMOVED' && parentMessageId !== undefined) {
    const stateWithParentSoftDeleted = update(state, parentMessageId, { isDeleted: true });

    return update(stateWithParentSoftDeleted, messageId, { isDeleted: true });
  }

  return state;
};
const markUnmarkAsQuestion = (
  state: Messages,
  action: ThreadMarkAsQuestionFulfilledAction | ThreadUnmarkAsQuestionFulfilledAction
) => {
  const { messageId } = action.payload;
  const message = state.fulfilled.byId[messageId];
  const { isQuestion, content } = message;
  const { title, body } = content as NormalMessageContent;
  let contentProps = {};
  if (isQuestion && !!title) {
    const newBody = prependTitleToRawContentState(title, body);
    contentProps = { title: undefined, body: newBody };
  }

  return update(state, messageId, {
    ...message,
    subtype: isQuestion ? 'changed_to_message' : 'changed_to_question',
    isQuestion: !isQuestion,
    content: {
      ...content,
      ...contentProps,
    },
  });
};
const addUpvoteToMessage = (state: Messages, action: AddUpvoteRejected | AddUpvotePending) => {
  const { messageId } = action.payload;
  const message = state.fulfilled.byId[messageId];
  const { upvotesTotalCount } = message;

  return update(state, messageId, {
    ...message,
    viewerHasUpvoted: true,
    upvotesTotalCount: upvotesTotalCount + 1,
  });
};
const removeUpvoteToMessage = (state: Messages, action: RemoveUpvoteRejected | RemoveUpvotePending) => {
  const { messageId } = action.payload;
  const message = state.fulfilled.byId[messageId];
  const { upvotesTotalCount } = message;

  return update(state, messageId, {
    ...message,
    viewerHasUpvoted: false,
    upvotesTotalCount: upvotesTotalCount > 0 ? upvotesTotalCount - 1 : 0,
  });
};
const markHasFileDeletionFailed = (state: Messages, hasFailedFileDeletions: boolean, messageId?: Message['id']) => {
  if (messageId) {
    const message = state.fulfilled.byId[messageId];

    return update(state, messageId, {
      ...message,
      hasFailedFileDeletions,
    });
  }

  return state;
};
const updatePollCloseFields = (
  state: Messages,
  { messageId, viewerCanClosePoll, isPollClosed }: PollCloseFulfilledAction['payload']
) => {
  const message = state.fulfilled.byId[messageId];
  const messageWithUpdatedPollCloseFields = {
    ...message,
    content: {
      ...message.content,
      viewerCanClosePoll,
      isPollClosed,
    },
  };

  return update(state, messageId, messageWithUpdatedPollCloseFields);
};

const disablePublishedEventAgentMessageEditing = (state: Messages, messageId: Message['id']): Messages => {
  if (!Object.prototype.hasOwnProperty.call(state.fulfilled.byId, messageId)) {
    return state;
  }

  if (state.fulfilled.byId[messageId].sender.schema !== 'eventAgents') {
    return state;
  }

  return update(state, messageId, { isEditable: false });
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<Messages, MessageAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'PUBLISH_MODERATED_THREAD.FULFILLED':
      return disablePublishedEventAgentMessageEditing(state, action.payload.threadStarterId);
    case 'PUBLISH_MODERATED_EVENT_AGENT_REPLY.FULFILLED':
      return disablePublishedEventAgentMessageEditing(state, action.payload.messageId);
    case 'THREAD.UPDATE_MODERATION_STATE':
      return action.payload.moderationState.toUpperCase() === 'PUBLISHED'
        ? disablePublishedEventAgentMessageEditing(state, action.payload.threadStarterId)
        : state;
    case 'MODERATED_EVENT_AGENT_REPLY.RECONCILED': {
      const nextState = update(state, action.payload.messageId, { isDeleted: action.payload.isDeleted });

      return !action.payload.isDeleted && action.payload.moderationState === undefined
        ? disablePublishedEventAgentMessageEditing(nextState, action.payload.messageId)
        : nextState;
    }
    case 'FILE.DELETE_REJECTED':
      return markHasFileDeletionFailed(state, true, action.payload.messageId);
    case 'MESSAGE.RESET_FAILED_FILE_DELETION':
      return markHasFileDeletionFailed(state, false, action.payload.messageId);
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD.MARK_AS_QUESTION_FULFILLED':
      return markUnmarkAsQuestion(state, action);
    case 'THREAD.UNMARK_AS_QUESTION_FULFILLED':
      return markUnmarkAsQuestion(state, action);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'MESSAGE.LIKE_PENDING':
      return addUserLikeToMessage(state, action);
    case 'MESSAGE.UNLIKE_PENDING':
      return removeUserLikeFromMessage(state, action);
    case 'MESSAGE.REALTIME_REACTION_ADDED':
      return addRealtimeReaction(state, action);
    case 'MESSAGE.REALTIME_REACTION_DELETED':
      return deleteRealtimeReaction(state, action);
    case 'MESSAGE_UPVOTES.ADD_UPVOTE_PENDING':
      return addUpvoteToMessage(state, action);
    case 'MESSAGE_UPVOTES.REMOVE_UPVOTE_PENDING':
      return removeUpvoteToMessage(state, action);
    case 'POLL.CLOSE_FULFILLED':
      return updatePollCloseFields(state, action.payload);
    case 'POLL.REOPEN_FULFILLED':
      return updatePollCloseFields(state, action.payload);
    case 'POLL.VOTE_PENDING':
      return optimisticallyUpdatePoll(state, action);
    case 'POLL.VOTE_REJECTED':
      return revertOptimisticallyUpdatedPoll(state, action);
    case 'POLL.LOAD_FULFILLED':
      return updatePoll(state, action);
    case 'MESSAGE.DELETE_FULFILLED':
      return markMessageAsDeleted(state, action.payload);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.messages);
    default:
      return state;
  }
};
export default reducer;
